/**
 * 3rd PARTY LIBRARIES
 **/
import axios, { AxiosError, AxiosInstance, AxiosResponse } from "axios";

/**
 * SHARED LIBRARIES
 **/
import Cookies from "shared/lib/helpers/cookies";
import { STATUS_TYPES } from "shared/lib/constants/statuses";
import {
  ResumeI,
  SalesResumeExperienceItemI,
} from "shared/ui/sales-resume/interfaces/resume";
import { IndustriesI } from "shared/lib/interfaces/industry";
import {
  AccountI,
  AccountStatusI,
  AccountUserListI,
} from "shared/lib/interfaces/account";

/**
 * INTERFACES
 */
import {
  ActiveAndPastCampaignsI,
  CampaignI,
  CampaignV3I,
} from "@/lib/types/campaign";
import { CallAccountI, CallContactI, LeadInfoI } from "@/lib/types/dialer";
import { ScheduleItemI } from "@/lib/types/dashboard/reminders-table";
import { ActivityEventI } from "@/lib/types/dialer/activity";
import { KanbanAccountI } from "@/lib/types/dashboard/accounts";
import {
  AccountExecutiveI,
  AccountHistoryI,
  AccountHistoryItemI,
  AccountUserDispositionI,
  CallRecordingI,
  ContactDetailI,
} from "@/lib/types/accounts";
import { AssessmentI } from "@/lib/types/assessment";
import { CampaignsTypeFilterI } from "@/lib/types/marketplace";
import { CampaignsSortByValueI } from "@/lib/types/marketplace/sort";
import { IngestionMappingI } from "@/lib/types/ingestion/request";
import { IngestionI } from "@/lib/types/ingestion";

/**
 * CONSTANTS
 */
import { DISPOSITIONS } from "@/constants/dispositions";
import { COOKIES_GLCC_ACCESS_TOKEN } from "@/constants/cookies";
import {
  ASSESSMENT_ANSWER_TYPES,
  ASSESSMENT_STATUS_TYPES,
} from "@/constants/assessment";
import { TRAINING_STATUS } from "@/constants/marketplace";
import { SALES_FLOOR_ENDPOINT_TYPES } from "@/constants/forum";

/**
 * HELPERS / UTILS
 */
import { interceptors } from "./interceptors";

/**
 * API ROUTES
 */
import {
  GetDashboardLeaderboardResponseI,
  UserDashboardAPIEndpoints,
} from "@/api/routes/dashboard";
import {
  UserOnboardingAPIEndpoints,
  UserOnboardingApiI,
} from "./routes/onboarding";
import {
  CoachingAPIEndpoints,
  CoachingApiEndpointsI,
} from "@/api/routes/coaching";
import {
  ImpersonateAPIEndpoints,
  ImpersonateApiI,
} from "@/api/routes/impersonate";

import { CountAPI, CountApiI } from "./routes/count";
import { ManageAPIEndpoints } from "./routes/manage";
import { WorkspaceAnalyticsAPIEndpoints } from "./routes/workspace-analytics";
import { ManageCampaignsAPIEndpoints } from "./routes/manage-campaigns";
import { ManageLeadIngestionsAPIEndpoints } from "./routes/manage-lead-ingestions";
import {
  ManageCampaignBlockControlsAPIEndpoints,
  ManageCampaignOpsAPIEndpoints,
} from "./routes/manage-campaign-ops";
import {
  ManageInsightsAPIEndpoints,
  ManageLeaderboardAPIEndpoints,
} from "./routes/manage-insights";
import { ManageRepsAPIEndpoints } from "./routes/manage-reps";
import { ManagePipelineAPIEndpoints } from "./routes/manage-pipeline";
import { ManageForecastAPIEndpoints } from "./routes/manage-forecast";
import { ManageEomAPIEndpoints } from "./routes/manage-eom";
import {
  IncentivesAPIEndpoints,
  RewardsAPIEndpoints,
} from "./routes/incentives";
import {
  createDialBlockManageApi,
  DialBlockManageApiI,
} from "shared/modules/dial-blocks-manage/api";
import { AccountAPIEndpoints } from "@/api/routes/account";
import { ListAPIEndpoints, ListApiI } from "@/api/routes/list";
import { NotificationsAPIEndpoints } from "@/api/routes/notifications";
import { DialBlocksAPIEndpoints } from "@/api/routes/dial-blocks";
import { TopCallsAPIEndpoints } from "@/api/routes/top-calls";
import { TopTenAPIEndpoints } from "@/api/routes/top-ten";
import { DailyTodosAPIEndpoints } from "@/api/routes/daily-todos";
import { BugReportsAPIEndpoints } from "@/api/routes/bug-reports";
import { UserAPIEndpoints, UserApiI } from "./routes/user";
import { PerformanceAPIEndpoints } from "@/api/routes/performance";
import { DialerAPIEndpoints, DialerApiI } from "@/api/routes/dialer";
import { AttachmentI, EmailAPIEndpoints } from "./routes/email";
import { ValueOfObjectFields } from "shared/lib/interfaces/utils";
import { MessagingAPIEndpoints } from "@/api/routes/messaging";
import { TrainingAPIEndpoints } from "@/api/routes/training";
import { TrainingModuleI } from "shared/lib/interfaces/training";
import {
  DefaultResponseI,
  GetPresignedUploadURLEndpointParamsI,
} from "shared/lib/interfaces/api";
import { ScriptI } from "shared/lib/interfaces/campaign";
import { QualificationCriteriaAPIEndpoints } from "@/api/routes/qualification-criteria";
import { ReferralsAPIEndpoints } from "@/api/routes/referrals";
import { VersionControlAPIEndpoints } from "@/api/routes/version-control";
import { CircleAPIEndpoints } from "@/api/routes/circle";
import { LeadImportReceiptI } from "@/components/sidebars/lead-import-receipt/interfaces";
import { BookedMeetingCalendarEventsEndpoints } from "@/api/routes/booked-meeting-calendar-events";
import { LeadImportReceiptFilterI } from "@/components/sidebars/lead-import-receipt/interfaces";
import { TIME_INTERVALS } from "shared/lib/constants/time";
import { DocsAPIEndpoints } from "@/api/routes/docs";
import { SchedulingEndpoints } from "@/api/routes/scheduling";
import { MeetingQualificationEndpoints } from "@/api/routes/meeting-qualification";
import {
  ProfileImageUploadCompleteI,
  ProfileImageUploadTicketI,
} from "shared/lib/helpers/profile-image-upload";

export type { CallRecordingI };
export type {
  GetUserResponseI,
  UserReferralSourceTypeI,
  UserUpdateResponseI,
  UserPatchParamsI,
} from "./routes/user";

export type {
  GetDialerLeadResponseI,
  DialerLeadTodoI,
  GetDialerLeadsTodoI,
  SaveContactCallbackRequestI,
  SaveContactCallbackResponseI,
  AddNextLeadRequestParamsI,
  AddNextLeadResponseI,
  AddAccountAndContactRequestParamsI,
  AddAccountAndContactResponseI,
  GetCallContextResponseI,
  BookMeetingResponseI,
} from "@/api/routes/dialer";

const applyInterceptors = interceptors;

export interface ResendVerificationEmailRequestParamsI {
  user_id: string;
}

export interface GetAuth0UserInfoRequestParamsI {
  user_id: string;
}

export interface GetAuth0UserInfoResponseI extends DefaultResponseI {
  user_info: {
    email_verified: boolean;
  };
}

export interface GetCampaignResponseI extends DefaultResponseI {
  campaign: CampaignI;
}

export interface GetCampaignV2ResponseI extends DefaultResponseI {
  campaign: { campaign_id: string; script: ScriptI };
}

export interface GetCampaignDefaultScriptResponseI extends DefaultResponseI {
  script_text: string;
}

// expects markdown string
export interface UpdateCampaignCustomScriptRequestParamsI {
  script_text: string;
}

// TODO remove
export interface GetCampaignsResponseI extends DefaultResponseI {
  campaigns: Array<CampaignI>;
}

export interface GetCampaignsV3ResponseI extends DefaultResponseI {
  campaigns: Array<CampaignV3I>;
  next_token?: string;
}

export type GetCampaignsActiveAndPastResponseI = ActiveAndPastCampaignsI;

export interface GetCampaignIcpTitlesResponseI extends DefaultResponseI {
  icp_titles: Array<string>;
}

export interface SignatureUploadURLResponseI extends DefaultResponseI {
  url: string;
}

export interface GetStripeURLResponse extends DefaultResponseI {
  link: string;
}

export interface GetStripeAccountCreationStatus extends DefaultResponseI {
  stripe_status: "not_connected" | "not_completed" | "completed";
}

export interface RawContractURLResponseI extends DefaultResponseI {
  url: string;
}

export interface GetLastSignatureDateResponseI extends DefaultResponseI {
  last_modified: string;
}

export interface ScheduleCertificationCallResponseI extends DefaultResponseI {
  meeting_url: string;
}

export interface getUnmatchedCallLeadsResponseI extends DefaultResponseI {
  contacts: Array<LeadInfoI>;
}

export interface GetDashboardRemindersResponseI {
  schedule_items: ScheduleItemI[];
}

export interface GetDashboardItemResponseI {
  contact: CallContactI;
  account: CallAccountI;
  event: ActivityEventI;
  campaign_name: string;
  campaign_id: string;

  calendly_uri?: string;
  call_id?: string;
}

export interface MeetingsItemI {
  id: string;
  ae_name: string;
  campaign_id: string;
  campaign_name: string;
  lead_company: string;
  lead_name: string;
  lead_title: string;
  payout: number;
  rating: number;
  status: ValueOfObjectFields<typeof STATUS_TYPES>;
  time: string;
}

export interface GetMeetingsResponseI {
  items: Array<MeetingsItemI[]>;
  next_token: string;
}

export interface GetCallHistoryStatsResponseI {
  current_call_streak: number;
  connected_calls_this_week: number;
  life_time_connected_calls: number;
  hours_calling_this_week: number;
}

export interface GetMeetingsStatsResponseI {
  life_time_money_earned: number;
  life_time_qualified_meetings: number;
  life_time_booked_meetings: number;
  no_show_rate: number;
  average_rating: number;
}

export interface GetHomepageStatsResponseI {
  connected_calls_today: number;
  life_time_booked_meetings: number;
  no_show_rate: number;
  average_rating: number;
}

export interface AnnouncementI {
  image_url: string;
  title: string;
  body: string;
  link: string;
  link_text: string;
}

export interface GetBannerAnnouncementResponseI {
  announcements: Array<AnnouncementI>;
}

export interface GetCampaignSampleRecordingsResponseI {
  recordings: CallRecordingI[];
}

export interface GetCampaignAttachmentsResponseI {
  attachments: AttachmentI[];
}

export interface GetResumeAssetUploadURLResponseI {
  upload_url: string;
}

export interface GetResumeResponseI {
  user_resume: ResumeI;
}

export type SalesResumeExperienceNewItemParamI = Pick<
  SalesResumeExperienceItemI,
  "campaign_id" | "description" | "show_campaign_stats"
>;

export type SalesResumeExperienceEditItemParamI = Pick<
  SalesResumeExperienceItemI,
  "id" | "description" | "show_campaign_stats"
>;

export interface ResumePatchParamsI {
  first_name?: string;
  last_name?: string;
  years_of_experience?: number;
  linkedin_url?: string;
  location?: string;
  phone?: string;
  email?: string;
  about?: string;
  experiences?: [];
  is_profile_public?: boolean;
  industries?: IndustriesI | null;
}

export interface GetAccountInfoResponseI {
  account: AccountI;
  account_executive: AccountExecutiveI;
  account_user_disposition: AccountUserDispositionI;
  user_lists: AccountUserListI[];
}

export interface GetAccountHistoryV2ResponseI {
  next_token?: string;
  activity_logs?: AccountHistoryItemI[];
}

export interface GetContactDetailsResponseI {
  next_token?: string;
  items?: ContactDetailI[];
}

interface GetKanbanAccountParamsI {
  campaigns: Array<string>;
}

export interface PartialGetKanbanAccountsEarningsResponseI {
  potential_earnings_usd: number;
  potential_earnings_backlog: number;
  potential_earnings_callbacks: number;
  potential_earnings_meetings: number;
  potential_earnings_feedback: number;
}

export interface GetKanbanAccountsResponseI extends PartialGetKanbanAccountsEarningsResponseI {
  items: Array<KanbanAccountI>;
  /** The left column is the caller's Top 10 instead of Nurture (C-16 S4). */
  top_ten_enabled?: boolean;
  potential_earnings_top_ten?: number;
}

export interface ModifyCallbackRequest {
  follow_up_time: string;
  notes: string;
}

export interface ModifyMeetingRequest {
  event_uri: string;
  notes: string;
}

export interface UpdateKanbanAccountParamsI {
  status: AccountStatusI;
  modify_callback_request?: ModifyCallbackRequest;
  modify_meeting_request?: ModifyMeetingRequest;
  cancel_meeting_request?: {
    note: string;
  };
}

// For adding contact to EXISTING account - only person location fields
export interface AddLeadToAccountParamsI {
  first_name?: string;
  last_name?: string;
  title?: string;
  phone?: string;
  mobile?: string;
  email?: string;
  linkedin_url?: string;
  list_id?: string;
  // Person location fields (contact's location)
  person_city?: string;
  person_state?: string;
  person_country?: string;
  person_postal_code?: string;
}

// For creating NEW account + contact - includes company location fields
export interface AddLeadAndAccountParamsI extends AddLeadToAccountParamsI {
  company_name?: string;
  // Company location fields
  company_city?: string;
  company_state?: string;
  company_country?: string;
  company_postal_code?: string;
  company_industry?: string;
}
export interface AddLeadNewAccountResponseI {
  account: AccountI;
}

export interface GetAccountCalendlyUriResponseI {
  uri: string;
}

export interface GetAccountCalendlyStartTimeResponseI {
  start_time: string;
}

export interface HistoryItemI extends AccountHistoryI {
  campaign_name: string;
  campaign_id: string;
  account_name: string;
  account_id: string;
}

export interface GetHistoryResponseI {
  items: HistoryItemI[];
  next_token: string;
}

export interface QualifiedMeetingsI {
  account_id?: string;
  campaign_id?: string;
  qualified_time?: string;
  campaign_name?: string;
  account_name?: string;
  prospect_name?: string;
  prospect_title?: string;
}
export interface GetQualifiedMeetingsResponseI {
  qualified_meetings: Array<QualifiedMeetingsI>;
  next_token?: string;
}

export type GetAssessmentResponseI = AssessmentI;

export interface GetAssessmentUploadUrlResponseI {
  url: string;
}

export interface UpdateUserSettingsParamsI {
  call_forwarding_disabled?: boolean;
  phone_number?: string;
}

export interface GetCampaignsCustomFilterPropsI {
  active_users_min?: number;
  active_users_max?: number;

  total_booked_last_2_weeks_min?: number;
  total_booked_last_2_weeks_max?: number;

  total_qualified_last_2_weeks_min?: number;
  total_qualified_last_2_weeks_max?: number;

  meeting_hold_rate_min?: number;
  meeting_hold_rate_max?: number;

  is_approved_to_call?: boolean;
  is_show_old_campaigns?: boolean;

  is_weekend_eligible?: boolean;

  training_status?: Array<ValueOfObjectFields<typeof TRAINING_STATUS>>;

  // TODO: ADD THIS BACK IN LATER.
  // Will need to update the custom filter menu form
  // training_status: Array<TrainingStatusTypesI>;
}

export interface GetCampaignsV3ParamsI {
  campaign_ids?: Array<string>;
  next_token?: string;

  filter?: {
    type?: Omit<CampaignsTypeFilterI, "all">;
    status?: Array<
      (typeof ASSESSMENT_STATUS_TYPES)[keyof typeof ASSESSMENT_STATUS_TYPES]
    >;
    custom?: GetCampaignsCustomFilterPropsI;
  };

  ascending?: boolean;
  sort?: CampaignsSortByValueI;
}

export interface GetCSVUploadURLRequestI {
  file_size: number;
  file_name: string;
  is_suppression?: boolean;
}

export interface GetCSVUploadURLResponseI {
  ingestion_id: string;
  upload_url: string;
}

export interface IngestionFieldI {
  field_name: string;
  field_sample_value: string;
  non_empty_count?: number; // Optional: 0 for CRM integrations
  total_rows?: number; // Optional: 0 for CRM integrations
  revenue_multiplier?: number; // 1000 or 1000000 if detected from column name
}

export type IngestionFieldTableI = "Account" | "Contact";

export interface IngestionAIFieldMappingI {
  data_column_name: string;
  glen_column_name: string;
  table_name: IngestionFieldTableI;
}

export interface GetIngestionFieldsResponseI extends DefaultResponseI {
  ingestion_fields: Array<IngestionFieldI>;
  ai_mappings: Array<IngestionAIFieldMappingI> | null;
}

export interface SubmitIngestionFieldsRequestI {
  list_id?: string | null;
  ingestion_mappings: Array<IngestionMappingI>;
}

export interface GetIngestionsResponseI {
  ingestions: Array<IngestionI>;
}

export interface GetIngestionResponseI {
  ingestion: IngestionI;
}

export interface GetLeadIngestionReceiptParamsI {
  next_token?: string | null;
  filter?: Exclude<LeadImportReceiptFilterI, "total_leads_imported"> | null;
}

export interface GetLeadIngestionReceiptStatsResponseI {
  total?: number;
  success?: number;
  no_valid_phone?: number;
  suppressed?: number;
  user_already_working?: number;
  duplicate?: number;
}

export interface GetLeadIngestionReceiptResponseI {
  receipts: LeadImportReceiptI[];
  next_token?: string | null;
}

export interface GetLeadIngestionReceiptExportParamsI {
  filters?: LeadImportReceiptFilterI[] | null;
}

// Response is the CSV content as a string
export type GetLeadIngestionReceiptExportResponseI = string;

export interface GetForumPostsConfigI {
  category?: string;
  endpoint_type: ValueOfObjectFields<typeof SALES_FLOOR_ENDPOINT_TYPES>;
}

export interface GetTrainingModuleResponseI extends DefaultResponseI {
  training_module: TrainingModuleI;
  training_module_presigned_url?: string;
}

export interface APII
  extends
    UserApiI,
    UserOnboardingApiI,
    DialerApiI,
    CountApiI,
    ListApiI,
    ImpersonateApiI,
    ReturnType<typeof UserDashboardAPIEndpoints>,
    ReturnType<typeof AccountAPIEndpoints>,
    ReturnType<typeof PerformanceAPIEndpoints>,
    ReturnType<typeof VersionControlAPIEndpoints>,
    ReturnType<typeof DocsAPIEndpoints> {
  Instance: AxiosInstance;

  email: ReturnType<typeof EmailAPIEndpoints>;

  notifications: ReturnType<typeof NotificationsAPIEndpoints>;

  dialBlocks: ReturnType<typeof DialBlocksAPIEndpoints>;
  topCalls: ReturnType<typeof TopCallsAPIEndpoints>;
  topTen: ReturnType<typeof TopTenAPIEndpoints>;
  dailyTodos: ReturnType<typeof DailyTodosAPIEndpoints>;
  bugReports: ReturnType<typeof BugReportsAPIEndpoints>;

  manage: ReturnType<typeof ManageAPIEndpoints>;

  // C-14 F3: the LGA block pages on the role zone (/user/v1/manage/...).
  manageDialBlocks: DialBlockManageApiI;

  // C-14 phase 2 W2: Team → Analytics (C-11's workspace analytics).
  manageWorkspaceAnalytics: ReturnType<typeof WorkspaceAnalyticsAPIEndpoints>;
  // C-14 phase 2 W4: Team → Activity and Team → Leaderboard.
  manageInsights: ReturnType<typeof ManageInsightsAPIEndpoints>;
  manageLeaderboard: ReturnType<typeof ManageLeaderboardAPIEndpoints>;
  // C-14 phase 2 W3: Team → Reps (/user/v1/manage/.../reps).
  manageReps: ReturnType<typeof ManageRepsAPIEndpoints>;
  incentives: ReturnType<typeof IncentivesAPIEndpoints>;
  rewards: ReturnType<typeof RewardsAPIEndpoints>;
  // C-16 S3: Team → Pipeline (/user/v1/manage/.../pipeline).
  managePipeline: ReturnType<typeof ManagePipelineAPIEndpoints>;
  // C-16 S3: Team → Forecast (/user/v1/manage/.../forecast).
  manageForecast: ReturnType<typeof ManageForecastAPIEndpoints>;
  // C-16 S2: Team → EOM (/user/v1/manage/.../eom).
  manageEom: ReturnType<typeof ManageEomAPIEndpoints>;

  // C-14 phase 2 W1: Team → Campaigns (campaign block settings).
  manageCampaigns: ReturnType<typeof ManageCampaignsAPIEndpoints>;
  // C-16 S8: Team → Health → Lead ingestion.
  manageLeadIngestions: ReturnType<typeof ManageLeadIngestionsAPIEndpoints>;
  // C-16 S8: Campaign Health's goals, triage log and launch checklist.
  manageCampaignOps: ReturnType<typeof ManageCampaignOpsAPIEndpoints>;
  // C-16 S8: Team → Calendar → Capacity pause and weekend days.
  manageCampaignBlockControls: ReturnType<
    typeof ManageCampaignBlockControlsAPIEndpoints
  >;

  training: ReturnType<typeof TrainingAPIEndpoints>;

  qualificationCriteria: ReturnType<typeof QualificationCriteriaAPIEndpoints>;

  referrals: ReturnType<typeof ReferralsAPIEndpoints>;

  coaching: CoachingApiEndpointsI;

  circle: ReturnType<typeof CircleAPIEndpoints>;

  messaging: ReturnType<typeof MessagingAPIEndpoints>;

  bookedMeetingCalendarEvents: ReturnType<
    typeof BookedMeetingCalendarEventsEndpoints
  >;

  scheduling: ReturnType<typeof SchedulingEndpoints>;

  meetingQualification: ReturnType<typeof MeetingQualificationEndpoints>;

  getCampaign(campaignId: string): Promise<AxiosResponse<GetCampaignResponseI>>;

  getCampaignV2(
    campaignId: string
  ): Promise<AxiosResponse<GetCampaignV2ResponseI>>;

  getCampaignDefaultScript(
    campaignId: string
  ): Promise<AxiosResponse<GetCampaignDefaultScriptResponseI>>;

  updateCampaignCustomScript(
    campaignId: string,
    params: UpdateCampaignCustomScriptRequestParamsI
  ): Promise<AxiosResponse<DefaultResponseI>>;

  resetCampaignCustomScript(
    campaignId: string
  ): Promise<AxiosResponse<DefaultResponseI>>;

  getCampaignsV3(
    params?: GetCampaignsV3ParamsI
  ): Promise<AxiosResponse<GetCampaignsV3ResponseI>>;

  getCampaignsActiveAndPast(): Promise<
    AxiosResponse<GetCampaignsActiveAndPastResponseI>
  >;

  getCampaignIcpTitles(
    campaignId: string
  ): Promise<AxiosResponse<GetCampaignIcpTitlesResponseI>>;

  getStripeURL(params: {
    return_url: string;
  }): Promise<AxiosResponse<GetStripeURLResponse>>;

  getStripeDashboardURL(params: {
    return_url: string;
  }): Promise<AxiosResponse<GetStripeURLResponse>>;

  getStripeAccountCreationStatus(): Promise<
    AxiosResponse<GetStripeAccountCreationStatus>
  >;

  getSignatureUploadURL(): Promise<AxiosResponse<SignatureUploadURLResponseI>>;
  getRawContractURL(): Promise<AxiosResponse<RawContractURLResponseI>>;
  getLastSignatureDate(): Promise<AxiosResponse<GetLastSignatureDateResponseI>>;

  resendVerificationEmail(
    params: ResendVerificationEmailRequestParamsI
  ): Promise<AxiosResponse<DefaultResponseI>>;
  getAuth0UserInfo(
    params: GetAuth0UserInfoRequestParamsI
  ): Promise<AxiosResponse<GetAuth0UserInfoResponseI>>;

  scheduleCertificationCall(
    campaignId: string
  ): Promise<AxiosResponse<ScheduleCertificationCallResponseI>>;

  getUnmatchedCallLeads(
    campaignId: string
  ): Promise<AxiosResponse<getUnmatchedCallLeadsResponseI>>;

  getDashboardReminders(params: {
    date: string;
  }): Promise<AxiosResponse<GetDashboardRemindersResponseI>>;

  getDashboardReminderItem(
    itemId: string
  ): Promise<AxiosResponse<GetDashboardItemResponseI>>;

  getCallHistoryStats(): Promise<AxiosResponse<GetCallHistoryStatsResponseI>>;

  getMeetingsStats(): Promise<AxiosResponse<GetMeetingsStatsResponseI>>;

  getHomepageStats(): Promise<AxiosResponse<GetHomepageStatsResponseI>>;

  getMeetingHistory(
    campaign_ids?: string[],
    status_filters?: Array<(typeof STATUS_TYPES)[keyof typeof STATUS_TYPES]>,
    next_token?: string
  ): Promise<AxiosResponse<GetMeetingsResponseI>>;

  getBannerAnnouncement(): Promise<
    AxiosResponse<GetBannerAnnouncementResponseI>
  >;

  getCampaignSampleRecordings(
    campaignId: string
  ): Promise<AxiosResponse<GetCampaignSampleRecordingsResponseI>>;

  getCampaignAttachments(
    campaignId: string
  ): Promise<AxiosResponse<GetCampaignAttachmentsResponseI>>;

  getResume(): Promise<AxiosResponse<GetResumeResponseI>>;

  updateResume(params: ResumePatchParamsI): Promise<AxiosResponse>;

  /** Stable profile photo, step 1 of 2 (see shared profile-image-upload). */
  getProfileImageUploadURLV2(
    params: GetPresignedUploadURLEndpointParamsI
  ): Promise<AxiosResponse<ProfileImageUploadTicketI>>;

  /** Stable profile photo, step 2 of 2: make the uploaded key the photo. */
  completeProfileImageUpload(params: {
    key: string;
  }): Promise<AxiosResponse<ProfileImageUploadCompleteI>>;

  addResumeSalesExperience(
    experienceItem: SalesResumeExperienceNewItemParamI
  ): Promise<AxiosResponse<DefaultResponseI>>;

  saveResumeSalesExperiences(
    experienceItems: Array<SalesResumeExperienceEditItemParamI>
  ): Promise<AxiosResponse<DefaultResponseI>>;

  getResumeGreetingUploadURL(
    params: GetPresignedUploadURLEndpointParamsI
  ): Promise<AxiosResponse<GetResumeAssetUploadURLResponseI>>;

  getLeaderboardData(): Promise<
    AxiosResponse<GetDashboardLeaderboardResponseI>
  >;

  getAccountInfo(
    campaignId: string,
    accountId: string
  ): Promise<AxiosResponse<GetAccountInfoResponseI>>;

  getContactDetails(
    campaignId: string,
    accountId: string,
    nextToken?: string
  ): Promise<AxiosResponse<GetContactDetailsResponseI>>;

  editAccountInfo(
    campaignId: string,
    accountId: string,
    data: {
      name?: string;
      industry?: string;
      website?: string;
      revenue_bucket?: string;
      state?: string;
      headcount?: string;
    }
  ): Promise<AxiosResponse>;

  editContactInfo(
    campaignId: string,
    contactId: string,
    params: {
      first_name?: string;
      last_name?: string;
      title?: string;
      phone?: string;
      mobile?: string;
      email?: string;
      linkedin_url?: string;
    }
  ): Promise<AxiosResponse>;

  addLeadToAccount(
    campaignId: string,
    accountId: string,
    data: AddLeadToAccountParamsI
  ): Promise<AxiosResponse>;

  addLeadNewAccount(
    campaignId: string,
    data: AddLeadAndAccountParamsI
  ): Promise<AxiosResponse<AddLeadNewAccountResponseI>>;

  deleteAccountLead(
    campaignId: string,
    contactId: string
  ): Promise<AxiosResponse>;

  getKanbanAccounts(
    params: GetKanbanAccountParamsI
  ): Promise<AxiosResponse<GetKanbanAccountsResponseI>>;

  updateKanbanAccount(
    accountId: string,
    params: UpdateKanbanAccountParamsI
  ): Promise<AxiosResponse<any>>;

  updateAccountWithCampaign(
    accountId: string,
    campaignId: string,
    params: UpdateKanbanAccountParamsI
  ): Promise<AxiosResponse<any>>;

  getAccountCalendlyUri(
    accountId: string,
    campaignId: string
  ): Promise<AxiosResponse<GetAccountCalendlyUriResponseI>>;

  getAccountCalendlyStartTime: (
    event_ur: string
  ) => Promise<AxiosResponse<GetAccountCalendlyStartTimeResponseI>>;

  toggleDoNotCall(
    accountId: string,
    campaignId: string,
    note: string
  ): Promise<AxiosResponse<any>>;

  messageAE(
    accountId: string,
    campaignId: string,
    params: { message: string }
  ): Promise<AxiosResponse>;

  getHistory(
    next_token?: string,
    call_disposition?: (typeof DISPOSITIONS)[keyof typeof DISPOSITIONS][],
    account_name_search_term?: string,
    campaigns?: string[]
  ): Promise<AxiosResponse<GetHistoryResponseI>>;

  getQualifiedMeetings(params?: {
    next_token?: string;
  }): Promise<AxiosResponse<GetQualifiedMeetingsResponseI>>;

  getAssessment(
    campaignId: string
  ): Promise<AxiosResponse<GetAssessmentResponseI> | AxiosError>;

  getAssessmentUploadUrl(
    campaignId: string,
    params: {
      content_type: "audio/webm" | "audio/wav" | "audio/mpeg";
      content_length: number;
      type: (typeof ASSESSMENT_ANSWER_TYPES)[keyof typeof ASSESSMENT_ANSWER_TYPES];
      question_id?: string;
    }
  ): Promise<AxiosResponse<GetAssessmentUploadUrlResponseI> | AxiosError>;

  validateAssessmentOnCompletion(
    campaignId: string,
    submissionId: string
  ): Promise<AxiosResponse<DefaultResponseI> | AxiosError>;

  updateUserSettings(params: UpdateUserSettingsParamsI): Promise<AxiosResponse>;

  getLeadCSVUploadURL(
    campaignId: string,
    params: GetCSVUploadURLRequestI
  ): Promise<AxiosResponse<GetCSVUploadURLResponseI>>;

  getIngestionFields(
    campaignId: string,
    ingestionId: string
  ): Promise<AxiosResponse<GetIngestionFieldsResponseI>>;

  submitIngestionFields(
    campaignId: string,
    ingestionId: string,
    params: SubmitIngestionFieldsRequestI
  ): Promise<AxiosResponse<DefaultResponseI>>;

  getIngestions(): Promise<AxiosResponse<GetIngestionsResponseI>>;

  getIngestion(
    ingestionId: string
  ): Promise<AxiosResponse<GetIngestionResponseI>>;

  getLeadIngestionReceiptStats(
    ingestionId: string
  ): Promise<AxiosResponse<GetLeadIngestionReceiptStatsResponseI>>;

  getLeadIngestionReceipt(
    ingestionId: string,
    params: GetLeadIngestionReceiptParamsI
  ): Promise<AxiosResponse<GetLeadIngestionReceiptResponseI>>;

  getLeadIngestionReceiptExport(
    ingestionId: string,
    params: GetLeadIngestionReceiptExportParamsI
  ): Promise<AxiosResponse<GetLeadIngestionReceiptExportResponseI>>;

  submitLeadQualityFeedback(
    campaignId: string,
    params: { feedback: string }
  ): Promise<AxiosResponse>;
}

const requests = (client: AxiosInstance): APII => ({
  Instance: client,

  ...UserAPIEndpoints(client),
  ...UserDashboardAPIEndpoints(client),
  ...UserOnboardingAPIEndpoints(client),
  ...DialerAPIEndpoints(client),
  ...CountAPI(client),
  ...AccountAPIEndpoints(client),
  ...ListAPIEndpoints(client),
  ...ImpersonateAPIEndpoints(client),
  ...PerformanceAPIEndpoints(client),
  ...EmailAPIEndpoints(client),
  ...VersionControlAPIEndpoints(client),
  ...DocsAPIEndpoints(client),

  email: EmailAPIEndpoints(client),
  notifications: NotificationsAPIEndpoints(client),
  dialBlocks: DialBlocksAPIEndpoints(client),
  topCalls: TopCallsAPIEndpoints(client),
  topTen: TopTenAPIEndpoints(client),
  dailyTodos: DailyTodosAPIEndpoints(client),
  bugReports: BugReportsAPIEndpoints(client),
  manage: ManageAPIEndpoints(client),
  manageDialBlocks: createDialBlockManageApi(client, "/v1/manage/workspace", {
    allWorkspacesPath: "/v1/manage/dial_blocks",
    // C-16 B-1: Health's "All workspaces" reads.
    allWorkspacesHealthPath: "/v1/manage",
  }),
  manageWorkspaceAnalytics: WorkspaceAnalyticsAPIEndpoints(client),
  // C-14 phase 2 W1
  manageCampaigns: ManageCampaignsAPIEndpoints(client),
  manageLeadIngestions: ManageLeadIngestionsAPIEndpoints(client),
  manageCampaignOps: ManageCampaignOpsAPIEndpoints(client),
  manageCampaignBlockControls: ManageCampaignBlockControlsAPIEndpoints(client),
  manageInsights: ManageInsightsAPIEndpoints(client),
  manageLeaderboard: ManageLeaderboardAPIEndpoints(client),
  manageReps: ManageRepsAPIEndpoints(client),
  incentives: IncentivesAPIEndpoints(client),
  rewards: RewardsAPIEndpoints(client),
  managePipeline: ManagePipelineAPIEndpoints(client),
  manageForecast: ManageForecastAPIEndpoints(client),
  manageEom: ManageEomAPIEndpoints(client),
  training: TrainingAPIEndpoints(client),
  qualificationCriteria: QualificationCriteriaAPIEndpoints(client),
  referrals: ReferralsAPIEndpoints(client),
  coaching: CoachingAPIEndpoints(client),
  circle: CircleAPIEndpoints(client),
  messaging: MessagingAPIEndpoints(client),
  bookedMeetingCalendarEvents: BookedMeetingCalendarEventsEndpoints(client),
  scheduling: SchedulingEndpoints(client),
  meetingQualification: MeetingQualificationEndpoints(client),

  getCampaign: (campaignId) => client.get(`/v1/campaign/${campaignId}`),
  getCampaignV2: (campaignId) => client.get(`/v2/campaign/${campaignId}`),
  getCampaignsV3: (params) => client.post(`/v3/campaigns`, { ...params }),

  getCampaignsActiveAndPast: () => client.get(`/v1/active_and_past_campaigns`),

  getCampaignIcpTitles: (campaignId) =>
    client.get(`/v1/campaign/${campaignId}/icp_titles`),

  getCampaignDefaultScript: (campaignId) =>
    client.get(`/v1/campaign/${campaignId}/customer_script`),
  updateCampaignCustomScript: (campaignId, params) =>
    client.post(`/v1/campaign/${campaignId}/script`, { ...params }),
  resetCampaignCustomScript: (campaignId) =>
    client.post(`/v1/campaign/${campaignId}/script_reset`),

  getStripeURL: (params) =>
    // Tested that this request requires minimum 7 seconds
    client.get(`/v1/stripe/link`, { params, timeout: 30000 }),
  getStripeDashboardURL: (params) =>
    client.get(`/v1/stripe/dashboard/link`, { params, timeout: 30000 }),
  getStripeAccountCreationStatus: () => client.get(`/v1/stripe/status`),

  getSignatureUploadURL: () => client.get(`/v1/signature/upload`),
  getRawContractURL: () => client.get(`/v1/signature/raw_contract`),
  getLastSignatureDate: () => client.get(`/v1/signature/exists`),

  resendVerificationEmail: (params) =>
    client.post(`/v1/resend-verification-email`, { ...params }),
  getAuth0UserInfo: (params) => {
    const data = JSON.stringify(params);
    return client.get(`/v1/session-info`, { data: data });
  },
  scheduleCertificationCall: (campaignId) =>
    client.post(`/v1/campaign/${campaignId}/meeting`),
  getUnmatchedCallLeads: (campaignId) =>
    client.get(`/v1/campaign/${campaignId}/contacts`),

  getDashboardReminders: (params) => client.get(`v1/dashboard`, { params }),
  getDashboardReminderItem: (itemId) =>
    client.get(`v1/dashboard/item/${itemId}`),

  getMeetingHistory: (campaignIds, statusFilters, nextToken) =>
    client.post(`v1/meeting_history`, {
      campaign_ids: campaignIds,
      status: statusFilters,
      next_token: nextToken,
    }),

  getCallHistoryStats: () => client.get(`v1/stats/call_history`),
  getMeetingsStats: () => client.get(`v1/stats/meetings`),
  getHomepageStats: () => client.get(`v1/stats/homepage`),

  getBannerAnnouncement: () => client.get(`v1/glcc-announcements`),

  getCampaignSampleRecordings: (campaignId) =>
    client.get(`v1/campaign/${campaignId}/sample_recordings`),

  getCampaignAttachments: (campaignId) =>
    client.get(`v1/campaign/${campaignId}/attachments`),

  getResume: () => client.get(`v1/resume`),
  updateResume: (params) => client.patch(`v1/resume`, { ...params }),

  getProfileImageUploadURLV2: (params) =>
    client.post(`v1/profile/upload/v2`, { ...params }),

  completeProfileImageUpload: (params) =>
    client.post(`v1/profile/upload/complete`, { ...params }),

  addResumeSalesExperience: (experienceItem) =>
    client.post("v1/resume/experience", experienceItem),

  saveResumeSalesExperiences: (experiences) =>
    client.patch("v1/resume/experience", {
      experiences,
    }),

  getResumeGreetingUploadURL: (params) =>
    client.post(`v1/greetings/upload`, { ...params }),

  getLeaderboardData: () => client.get(`v2/leaderboard`),

  getAccountInfo: (campaignId, accountId) =>
    client.get(`v1/campaign/${campaignId}/account/${accountId}`),

  getContactDetails: (campaignId, accountId, nextToken) =>
    client.post(`v1/campaign/${campaignId}/account/${accountId}/details`, {
      next_token: nextToken,
    }),

  editAccountInfo: (campaignId, accountId, data) =>
    client.patch(`v1/campaign/${campaignId}/account/${accountId}`, {
      patch_account: { ...data },
    }),

  addLeadToAccount: (campaignId, accountId, data) =>
    client.post(`v1/campaign/${campaignId}/account/${accountId}/new_contact`, {
      ...data,
    }),

  addLeadNewAccount: (campaignId, data) =>
    client.post(`v2/campaign/${campaignId}/account_and_contact`, {
      ...data,
    }),

  deleteAccountLead: (campaignId, contactId) =>
    client.delete(`v1/campaign/${campaignId}/contact/${contactId}`),

  getKanbanAccounts: (params) => client.post("v1/open_accounts", params),

  updateKanbanAccount: (accountId, params) =>
    client.post(`v1/account_user_dispo/${accountId}/update_status`, params),

  updateAccountWithCampaign: (accountId, campaignId, params) =>
    client.post(
      `v1/campaign/${campaignId}/account/${accountId}/dispo_status`,
      params
    ),

  getAccountCalendlyUri: (accountId, campaignId) =>
    client.get(`v1/campaign/${campaignId}/account/${accountId}/calendly_uri`),
  getAccountCalendlyStartTime: (event_uri) =>
    client.get(`/v1/calendly/start_time?event_uri=${event_uri}`),

  toggleDoNotCall: (accountId, campaignId, note: string) =>
    client.post(
      `v1/campaign/${campaignId}/account/${accountId}/toggle_do_not_call`,
      {
        note,
      }
    ),

  messageAE: (accountId, campaignId, params) =>
    client.post(`v1/campaign/${campaignId}/account/${accountId}/message_ae`, {
      ...params,
    }),

  editContactInfo: (campaignId, contactId, params) =>
    client.patch(`v1/campaign/${campaignId}/contact/${contactId}`, {
      patch_contact: params,
    }),

  getHistory: (
    next_token,
    call_disposition,
    account_name_search_term,
    campaigns
  ) =>
    client.post(`v1/history`, {
      next_token: next_token,
      account_name_search_term: account_name_search_term,
      call_disposition: call_disposition,
      campaigns,
    }),

  getQualifiedMeetings: ({ next_token } = {}) =>
    client.post(`/v1/trophies/meetings`, {
      next_token,
    }),

  getAssessment: (campaignId) =>
    client.get(`/v1/campaign/${campaignId}/audition`),
  getAssessmentUploadUrl: (campaignId, params) =>
    client.post(`/v1/campaign/${campaignId}/audition/upload`, { ...params }),
  validateAssessmentOnCompletion: (campaignId, submissionId) =>
    client.post(`/v1/campaign/${campaignId}/audition/${submissionId}/validate`),

  updateUserSettings: (params) =>
    client.patch(`v1/settings`, { settings: params }),

  getLeadCSVUploadURL: (campaignId, params) =>
    client.post(`/v1/campaign/${campaignId}/lead_csv_url`, {
      ...params,
    }),

  getIngestionFields: (campaignId, ingestionId) =>
    client.get(
      `/v1/campaign/${campaignId}/ingestion/${ingestionId}/ingestion_fields`,
      { timeout: 60000 }
    ),

  submitIngestionFields: (campaignId, ingestionId, params) =>
    client.post(
      `/v1/campaign/${campaignId}/ingestion/${ingestionId}/ingestion_mappings`,
      { ...params }
    ),

  getIngestions: () => client.get(`/v1/ingestions`),
  getIngestion: (ingestionId) => client.get(`v1/ingestion/${ingestionId}`),

  getLeadIngestionReceiptStats: (ingestionId) =>
    client.get(`/v1/ingestion/${ingestionId}/lead_receipts_stats`),

  getLeadIngestionReceipt: (ingestionId, params) =>
    client.post(`/v1/ingestion/${ingestionId}/lead_receipts`, { ...params }),

  getLeadIngestionReceiptExport: (ingestionId, params) =>
    client.post(`/v1/ingestion/${ingestionId}/lead_receipts/export`, {
      ...params,
    }),

  submitLeadQualityFeedback: (campaignId: string, params) =>
    client.post(`v1/campaign/${campaignId}/lead_feedback`, { ...params }),
});

export const glencocoServerAPI = ({
  token,
  req,
  res,
}: {
  token?: string;
  req?: any;
  res?: any;
}) => {
  let auth0Token;

  if (token) {
    auth0Token = token;
  } else if (req && res) {
    const cookies = Cookies({ req, res });

    auth0Token = cookies.getCookie(COOKIES_GLCC_ACCESS_TOKEN);
  }

  const API = axios.create({
    // TODO Before release change https://dev-glensocket.glencoco.com/user to https://dev-glensocket.glencoco.com
    baseURL: process.env.NEXT_PUBLIC_API_DOMAIN,
    maxRedirects: 0,
    timeout: process.env.NEXT_PUBLIC_ENV === "production" ? 10000 : 30000,
    responseType: "json",
    headers: {
      Accept: "*/*",
      "Content-Type": "application/json",
      "accept-encoding": "*",
      Authorization: `Bearer ${auth0Token}`,
    },
  });

  applyInterceptors(API);

  return requests(API);
};

export const glencocoClientAPI = () => {
  const { ONE_SECOND } = TIME_INTERVALS;

  const API = axios.create({
    // TODO Before release change https://dev-glensocket.glencoco.com/user to https://dev-glensocket.glencoco.com
    baseURL: process.env.NEXT_PUBLIC_API_DOMAIN,
    maxRedirects: 0,
    timeout:
      process.env.NEXT_PUBLIC_ENV === "production"
        ? ONE_SECOND * 20
        : ONE_SECOND * 100,
    responseType: "json",
    headers: {
      Accept: "*/*",
      "Content-Type": "application/json",
    },
  });

  applyInterceptors(API);

  return requests(API);
};

export const glencocoPublicAPI = () => {
  const API = axios.create({
    // TODO before release switch to default domain value
    // https://dev-glensocket.glencoco.com
    // on release don't forget to update env vars
    // For now is just a workaround
    baseURL: process.env.NEXT_PUBLIC_API_DOMAIN?.replace(`/user`, ""),
    maxRedirects: 0,
    timeout: process.env.NEXT_PUBLIC_ENV === "production" ? 20000 : 30000,
    responseType: "json",
    headers: {
      Accept: "*/*",
      "Content-Type": "application/json",
    },
  });

  applyInterceptors(API);

  return requests(API);
};
